<template>
  <div class="foot-bg" :class="{en : locale == 'en-US'}">
    <div class="wrap">
      <div class="foot flex">
        <ul class="foot-left flex">
          <li v-for="item in footerData" :key="item.id">
            <h3 class="foot-title" v-if="item.childrenList.length == 0">
              <nuxt-link
                @click="toLink(item)"
                :to="localePath(item.link)"
                :target="item.link.includes('http') ? '_blank' : ''"
                :rel="value.link.includes('http')?'nofollow noopener noreferrer':''"
              >
                {{ item.name }}
              </nuxt-link>
            </h3>
            <h3 class="foot-title" v-else>{{ item.name }}</h3>
            <div class="foot-sub">
              <nuxt-link
                v-for="value in item.childrenList"
                :key="value.id"
                @click="toLink(value)"
                :to="localePath(value.link)"
                :target="value.link.includes('http') ? '_blank' : ''"
                :rel="value.link.includes('http')?'nofollow noopener noreferrer':''"
              >
                {{ value.name }}
              </nuxt-link>
            </div>
          </li>
        </ul>
        <div class="foot-right flex text-right">
          <div class="foot-text">
            <div class="foot-logo"><img :src="footerConfig.logo || ''" alt="" /></div>
            <div class="foot-con font16 color-f">
              <p>{{ footerConfig.phone || '' }}</p>
              <p>{{ footerConfig.address || '' }}</p>
            </div>
          </div>
          <div class="foot-wx">
            <img :src="footerConfig.qrCode || ''" alt="" />
          </div>
        </div>
      </div>
      <div class="copyright">
        <p>{{ copyrightText }}</p>
        <p>
          <nuxt-link :to="icpLink" target="_blank" rel="nofollow noopener noreferrer">{{ icpText }}</nuxt-link>{{ licenseText }}
        </p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'
import useServerRequest from '@/composables/useServerRequest'
import type { configResponse, FooterMenuItem, FooterMenuRaw, FooterConfig } from '~/types'
import { useI18n } from 'vue-i18n' // 导入 useI18n
const { locale, t } = useI18n() // 获取当前 i18n 语言（如 zh-CN/en-US）
import { useLocalePath, useRuntimeConfig } from '#imports'
const config = useRuntimeConfig()
const localePath = useLocalePath()
const cmsurlStrategy = config?.public?.cmsStrategy

/** 接口未下发时的备案/版权兜底（与改接口前硬编码一致） */
const FOOTER_COPYRIGHT_FALLBACK =
  'Copyright © 2009-2026 北京墨迹风云科技股份有限公司 All Right Reserved ©2026 墨迹风云'
const FOOTER_ICP_LINK_FALLBACK = 'https://beian.miit.gov.cn/#/Integrated/index'
const FOOTER_ICP_TEXT_FALLBACK = '京ICP备10021324号'
const FOOTER_LICENSE_FALLBACK =
  '（京）网药械信息备字(2022)第00158号京公网安备 11010502030670号'

const toLink = (item: FooterMenuItem) => {
  sendBuryClient({
    key: `${item.eventKey || ''}_button`,
    name: item.name,
    module: 'footer'
  })
  // window.open(item.link)
}

// 获取导航配置
const footerData = ref<FooterMenuItem[]>([])
const footerConfig = ref<FooterConfig>({})
const formatNav = (navList: FooterMenuRaw[]): FooterMenuItem[] => {
  return navList.map((item) => ({
    id: item.id,
    name: item.title,
    link: item.link,
    eventKey: item.eventKey,
    childrenList: item.children?.length ? formatNav(item.children) : []
  }))
}

const copyrightText = computed(
  () => footerConfig.value.copyrightText?.trim() || FOOTER_COPYRIGHT_FALLBACK,
)
const icpLink = computed(
  () => footerConfig.value.icpLink?.trim() || FOOTER_ICP_LINK_FALLBACK,
)
const icpText = computed(
  () => footerConfig.value.icpText?.trim() || FOOTER_ICP_TEXT_FALLBACK,
)
const licenseText = computed(
  () => footerConfig.value.licenseText?.trim() || FOOTER_LICENSE_FALLBACK,
)

try {
  const { data: footerConfigData } = await useServerRequest<configResponse>(
    'COMMON_CONFIG-footer',
    `${cmsurlStrategy}/getPageConfig`,
    {
      method: 'GET',
      params: {
        pageCode: 'COMMON_CONFIG',
      },
      language: locale.value,
    },
  )
  const getInfoData = footerConfigData.value?.data?.pageValue as configResponse['pageValue']
  footerData.value = formatNav(getInfoData?.footer?.menuList || [])
  footerConfig.value = getInfoData?.footer || {}
} catch {
  // 配置拉取失败时保留空页脚，避免阻塞页面渲染
}
</script>
<style lang="scss" scoped>
.foot-bg {
  position: relative;
  min-height: 335px;
  padding: 46px 0;
  background-color: rgba(21, 27, 41, 1);
  z-index: 1;
}
.foot-title {
  font-size: 16px;
  color: #ffffff;
  line-height: 22px;
  margin-bottom: 14px;
  a {
    color: #1a1a1a;
    &:hover {
      color: var(--org-color);
    }
  }
}
.foot-left {
  list-style: none;
  flex: auto;
  overflow: hidden;
  margin-right: 2%;
  li {
    width: 65px;
    margin-right: 90px;
    white-space: nowrap;
    
  }
  .foot-sub {
    a {
      display: block;
      font-size: 16px;
      color: #ffffff;
      line-height: 28px;
      opacity: 0.7;
      transition: all 0.3s;
      &:hover {
        opacity: 1;
      }
    }
  }
}
.foot-right {
  flex-shrink: 0;
  width: 40%;
  max-width: 500px;
  color: #ffffff;
  line-height: 28px;
  margin-left: auto;
  justify-content: flex-end;
  .foot-text {
    .foot-logo {
      max-width: 152px;
      width: auto;
      margin-top: 2px;
      margin-bottom: 16px;
      margin-left: auto;
      img {
        display: inline-block;
        max-width: 100%;
        height: 24px;
        width: auto;
        object-fit: contain;
      }
    }
    .foot-con {
      opacity: 0.5;
    }
  }
  .foot-wx {
    margin-left: 25px;
    img {
      display: block;
      width: 120px;
    }
  }
}
.go-top {
  border-radius: 0;
  box-shadow: none;
  width: fit-content;
  height: 50px;
  font-size: 16px;
  padding: 0 25px;
  line-height: 1;
  transition: all 0.3s;
  img {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
}
.copyright{
  font-size: 14px;
  color: rgba($color: #FFFFFF, $alpha: 0.6);
  line-height: 1.8;
  margin-top: 50px;
  text-align: center;
  a,
  span{
    display: inline-block;
    vertical-align: middle;
    margin: 0 6px;
  }
  a{
    color: rgba($color: #FFFFFF, $alpha: 0.6);
    transition: all .3s linear;
    &:hover{
      color: rgba($color: #FFFFFF, $alpha: 0.8);
    }
  }
}
// en
.en{
  .foot-left{
    margin-right: 0;
    li{
      width: fit-content;
      margin-right: 30px;
      white-space: normal;
      &:last-child{
        margin-right:20px;
      }
      a{
        line-height: 20px;
        margin: 8px 0;
      }
    }
  }
  .foot-right{
    max-width: 460px;
  }
}
@media (max-width: 1600px) {
}
@media (max-width: 1400px) {
}
@media (max-width: 1200px) {
  .foot-left {
    li {
      margin-right: 12%;
    }
  }
}
@media (max-width: 991px) {
  .foot-bg {
    position: relative;
    padding: 0.46rem 0 0.6rem;
    min-height: 0;
    z-index: 1;
  }
  .foot {
    display: block;
  }
  .foot-left {
    justify-content: space-between;
    margin-right: 0;
    margin-bottom: 0.3rem;
    li {
      margin: 0;
    }
  }
  .foot-right {
    width: 100%;
    max-width: none;
    justify-content: flex-end;
    flex-direction: row-reverse;
    line-height: 20px;
    text-align: left;
    .foot-text {
      .foot-logo {
        margin-left: 0;
        margin-right: auto;
        margin-bottom: 0.16rem;
      }
      .foot-con {
        opacity: .8;
        p {
          margin: 5px 0;
        }
      }
    }
    .foot-wx {
      margin-left: 0;
      margin-right: 0.2rem;
    }
  }
}
@media (max-width: 767px) {
  .foot-left {
    display: block;
    // margin-bottom: 0;
    li {
      width: 100%;
      white-space: normal;
      margin-right: 0;
      margin-bottom: 0.2rem;
    }
    .foot-title {
      line-height: 24px;
      margin-bottom: 0;
    }
    .foot-sub {
      margin: 0 -8px;
      a {
        display: inline-block;
        vertical-align: middle;
        font-size: 14px;
        margin: 2px 8px;
      }
    }
  }

  .go-top {
    right: 16px !important;
    bottom: 16px !important;
    width: 36px;
    height: 36px;
    font-size: 0;
    padding: 0;
    border-radius: 6px;
    line-height: 1;
    transition: all 0.3s;
    img {
      margin-right: 0;
    }
  }
  .en{
    .foot-left{
      li{
        a{
          margin: 2px 8px;
        }
      }
    }
  }
}
@media (max-width: 640px) {
  .foot-right {
    flex-direction: row-reverse;
    line-height: 20px;
    text-align: left;
    .foot-text {
      .foot-logo {
        margin-left: 0;
        margin-right: auto;
        margin-bottom: 0.16rem;
      }
      .foot-con {
        p {
          margin: 5px 0;
        }
      }
    }
    .foot-wx {
      margin-left: 0;
      margin-right: 0.2rem;
    }
  }
  .copyright{
    margin-top: 30px;
  }
}
@media (max-width: 460px) {
}
</style>
